import React from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable, ColumnHelper } from "@/components/data-table"
import { api } from "@/lib/api"
import { AlertTriangle, RefreshCw } from "lucide-react"

export default function ErrorsPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["error-events"],
    queryFn: () => api.getEvents({ type: "error" }),
  })

  const { data: stats } = useQuery({
    queryKey: ["event-stats"],
    queryFn: () => api.getEventStats(),
  })

  const colHelper = ColumnHelper()
  const columns = React.useMemo(
    () => [
      colHelper.text("id", { label: "ID" }),
      colHelper.text("type", { label: "Type" }),
      colHelper.text("message", { label: "Message" }),
      colHelper.text("source", { label: "Source" }),
      colHelper.date("created_at", { label: "Time" }),
    ],
    []
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Error Logs</h1>
            <p className="text-muted-foreground">System errors and exceptions</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.data?.errors || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats?.data?.critical || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Warnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{stats?.data?.warnings || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Last 24h</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.data?.last_24h || 0}</div>
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={columns}
        data={data?.data || []}
        searchKey="message"
        searchPlaceholder="Search errors..."
        onRefresh={refetch}
        isLoading={isLoading}
        emptyMessage="No errors recorded"
      />
    </div>
  )
}
